import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardListIcon, CopyIcon, ExternalLinkIcon, LinkIcon, PencilIcon, PlusIcon, PowerIcon, Trash2Icon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { createForm, deleteForm, duplicateForm, setFormStatus } from '../../services/formService';
import { formatDate } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Dropdown } from '../../components/ui/Dropdown';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Tag } from '../../components/ui/Tag';
import { toast } from '../../components/ui/Toast';
import { copyToClipboard, formUrl } from '../../utils/clipboard';
export function Forms() {
  const navigate = useNavigate();
  const {
    organizationId,
    forms,
    leads,
    landings
  } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  async function handleCreate() {
    if (!newName.trim()) return;
    setSaving(true);
    const form = await createForm(organizationId, newName.trim());
    setSaving(false);
    setCreating(false);
    setNewName('');
    navigate(`/app/formularios/${form.id}`);
  }
  return <>
      <PageHeader title="Formularios" description="Crea las preguntas que verán tus potenciales clientes." actions={<Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1007" data-mp="1mxcqrdyq1hgs" />} onClick={() => setCreating(true)} data-id="element-1006" data-mp="1mwtjcr6p6bh0">
            Crear formulario
          </Button>} data-id="element-1005" data-mp="1rvo1dqwubw02" />

      {forms.length === 0 ? <EmptyState icon={<ClipboardListIcon className="h-5 w-5" data-id="element-1009" data-mp="2fl8uh2mpk9n2" />} title="Todavía no tienes formularios" description="Crea tu primer formulario y asócialo a una landing para empezar a recibir leads." action={<Button onClick={() => setCreating(true)} data-id="element-1010" data-mp="33i592km232lz">Crear formulario</Button>} data-id="element-1008" data-mp="2flqnzrhzk9ra" /> : <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-1011" data-mp="2pq748lzdnsvm">
          {forms.map(form => {
        const responses = leads.filter(l => l.origin.formId === form.id).length;
        const linked = landings.filter(l => l.formId === form.id);
        return <li key={form.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1012" data-mp="2fr9jzv8zilif">
                <div className="flex items-start justify-between gap-3" data-id="element-1013" data-mp="2ksfenym7giul">
                  <div className="min-w-0" data-id="element-1014" data-mp="26so3s6qzjikt">
                    <Link to={`/app/formularios/${form.id}`} className="block truncate text-base font-semibold text-foreground hover:text-primary" data-id="element-1015" data-mp="26tpgefnngd4n">
                      {form.name}
                    </Link>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground" data-id="element-1016" data-mp="91l2wot91r0z">
                      {form.description || 'Sin descripción'}
                    </p>
                  </div>
                  <Dropdown align="right" trigger={<span className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-surface" data-id="element-1018" data-mp="1pitmfzgx1306">
                        ⋯
                      </span>} items={[{
              label: 'Editar',
              icon: <PencilIcon className="h-4 w-4" data-id="element-1019" data-mp="1yigs4xgoguz1" />,
              onSelect: () => navigate(`/app/formularios/${form.id}`)
            }, {
              label: 'Vista previa',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-1020" data-mp="288s2b1qyg0ed" />,
              onSelect: () => window.open(`/formulario/${form.slug}`, '_blank')
            }, {
              label: 'Copiar enlace',
              // Copia fiable con confirmación visible.
              icon: <LinkIcon className="h-4 w-4" data-id="element-1021" data-mp="2h9zqgqzrbkuq" />,
              onSelect: async () => {
                const ok = await copyToClipboard(formUrl(form.slug));
                if (ok) toast.success('Enlace copiado');else toast.error('No se ha podido copiar el enlace');
              }
            }, {
              label: 'Duplicar',
              icon: <CopyIcon className="h-4 w-4" data-id="element-1022" data-mp="tbfj83ln5iz8" />,
              onSelect: () => duplicateForm(form.id)
            }, {
              label: form.status === 'activo' ? 'Desactivar' : 'Activar',
              icon: <PowerIcon className="h-4 w-4" data-id="element-1023" data-mp="139lei2cxzqv4" />,
              onSelect: () => setFormStatus(form.id, form.status === 'activo' ? 'inactivo' : 'activo')
            }, {
              label: 'Eliminar',
              icon: <Trash2Icon className="h-4 w-4" data-id="element-1024" data-mp="akoa935kfuld" />,
              destructive: true,
              onSelect: () => setToDelete(form.id)
            }]} data-id="element-1017" data-mp="1pi0nvls41jt4" />
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm" data-id="element-1025" data-mp="fi7mxymm7zb7">
                  <div data-id="element-1026" data-mp="kcbuok3pgbdc">
                    <dt className="text-xs text-muted-foreground" data-id="element-1027" data-mp="kckaokv4jwsr">Preguntas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1028" data-mp="3j0hgmp66npop">
                      {form.questions.length}
                    </dd>
                  </div>
                  <div data-id="element-1029" data-mp="3ggl5sdxwumi2">
                    <dt className="text-xs text-muted-foreground" data-id="element-1030" data-mp="3gft7gvcxiao3">Respuestas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1031" data-mp="3bi9tlwk57zwo">{responses}</dd>
                  </div>
                  <div data-id="element-1032" data-mp="36h3wznnd3shv">
                    <dt className="text-xs text-muted-foreground" data-id="element-1033" data-mp="36gar0ow8970s">Creado</dt>
                    <dd className="text-muted-foreground" data-id="element-1034" data-mp="31i96dzj5kzn7">
                      {formatDate(form.createdAt)}
                    </dd>
                  </div>
                  <div data-id="element-1035" data-mp="2sr0jpm6gn94k">
                    <dt className="text-xs text-muted-foreground" data-id="element-1036" data-mp="2sqgy9vhlq2ls">Modificado</dt>
                    <dd className="text-muted-foreground" data-id="element-1037" data-mp="2nre45635tgsl">
                      {formatDate(form.updatedAt)}
                    </dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border pt-4" data-id="element-1038" data-mp="2iqq82602q9ri">
                  <Tag data-id="element-1039" data-mp="29ruycjxtmhjg">
                    {form.status === 'activo' ? 'Activo' : form.status === 'borrador' ? 'Borrador' : 'Inactivo'}
                  </Tag>
                  {linked.map(landing => <Tag key={landing.id} data-id="element-1040" data-mp="1z8vozbzw6u07">{landing.name}</Tag>)}
                  {linked.length === 0 && <span className="text-xs text-muted-foreground" data-id="element-1041" data-mp="2475lbpxcs0ne">
                      Sin landing asociada
                    </span>}
                </div>
              </li>;
      })}
        </ul>}

      <Modal open={creating} onClose={() => setCreating(false)} title="Nuevo formulario" footer={<div className="flex justify-end gap-2" data-id="element-1043" data-mp="2iva5kyc20k8a">
            <Button variant="ghost" onClick={() => setCreating(false)} data-id="element-1044" data-mp="2mw3rjm5xroyj">
              Cancelar
            </Button>
            <Button onClick={handleCreate} loading={saving} disabled={!newName.trim()} data-id="element-1045" data-mp="295qlxqgiomcd">
              Crear
            </Button>
          </div>} data-id="element-1042" data-mp="1v5ftiizk6zp3">
        <Input id="nombre-form" label="Nombre del formulario" placeholder="Solicitud de presupuesto" value={newName} onChange={e => setNewName(e.target.value)} data-id="element-1046" data-mp="2e27b8nzgd6r1" />
      </Modal>

      <ConfirmDialog open={Boolean(toDelete)} title="Eliminar formulario" description="Las landings asociadas se quedarán sin formulario. Esta acción no se puede deshacer." onCancel={() => setToDelete(null)} onConfirm={async () => {
      if (toDelete) await deleteForm(toDelete);
      setToDelete(null);
    }} data-id="element-1047" data-mp="36l4ex5x5zqns" />
    </>;
}